import React, { useMemo, useRef, useState } from 'react';
import { SxProps } from '@mui/material';
import clsx from 'clsx';

import dashboardImage from '@atom/components/common/svgIcons/dashboardPortal.svg';
import { useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { Icon, Popover } from '@atom/mui';
import { AppsIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { Portal } from '@atom/types/portals';
import { getPortals } from '@atom/utilities/portalUtilities';

import classes from './navigation.module.css';

const styles = {
  iconStyle: {
    padding: '0 0.5rem',
    color: colors.neutral.gray,
  },
  dashboardIcon: {
    marginRight: '1rem',
    width: '2.5rem',
    height: '2.5rem',
  },
};

interface Props {
  pathname: string;
}

const sx: SxProps = {
  marginTop: '10px',
  width: '224px',
};

const PortalMenu = ({ pathname }: Props) => {
  const [, { navigate }] = useNavigation();

  const preferences = usePreferences();
  const landingPortal = getPortals(preferences)[preferences.landingPortal];

  const anchor = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState<boolean>(false);

  const portals = useMemo(() => {
    return getPortals(preferences);
  }, [getPortals, preferences]);

  const currentPortal = useMemo(() => {
    const path = pathname.split('/')[1];

    switch (path) {
      case 'dashboard':
        return portals[Portal.DASHBOARD];
      case 'team':
        return portals[Portal.TEAM];
      case 'inventoryTypes':
      case 'inventory-requests':
      case 'inventory':
        return portals[Portal.INVENTORY];
      case 'workOrders':
      case 'workExports':
      case 'form-repository':
      case 'work-template-repository':
      case 'form-instance':
      case 'form-builder':
      case 'work-templates':
        return portals[Portal.WORK];
      case 'analytics':
        return portals[Portal.ANALYTICS];
      case 'schedule':
        return portals[Portal.SCHEDULE];
      // admin routes can belong to different portals
      case 'admin': {
        const adminPath = pathname.split('/')[2];

        switch (adminPath) {
          case 'user-types':
            return portals[Portal.TEAM];
          case 'inventoryTypes':
            return portals[Portal.INVENTORY];
          default:
            return landingPortal;
        }
      }
      case 'budget':
      case 'budgets':
        return portals[Portal.BUDGETING];
      case 'map':
        return portals[Portal.MAP];
      default:
        return landingPortal;
    }
  }, [pathname, portals]);

  const handlePortalClick = (event: React.MouseEvent, path: string) => {
    event.preventDefault();
    setOpen(false);
    navigate(path);
  };

  const portalOrder = [
    Portal.INVENTORY,
    Portal.TEAM,
    Portal.WORK,
    Portal.ANALYTICS,
    Portal.SCHEDULE,
    Portal.MAP,
    Portal.BUDGETING,
  ];

  const enabledPortals = portalOrder.filter(
    // @ts-ignore
    portalKey => !getPortals(preferences)[portalKey]?.disabled,
  );

  const dashboardPortal = getPortals(preferences)[Portal.DASHBOARD];

  return (
    <div className={classes.portalWrapper} ref={anchor}>
      <div className={classes.portalContainer}>
        <a
          href={currentPortal?.urlPath}
          onClick={event => handlePortalClick(event, currentPortal?.urlPath)}
        >
          <div className={clsx(classes.portalIcon, classes.header)}>
            <img src={currentPortal?.image} alt={currentPortal?.description} />
          </div>
        </a>
        <Icon
          onClick={() => setOpen(true)}
          style={styles.iconStyle}
          icon={AppsIcon}
          data-cy="portalNavigationButton"
        />
      </div>
      <Popover
        open={open}
        anchorEl={anchor.current}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={() => setOpen(false)}
        slotProps={{ paper: { sx } }}
      >
        <div className={classes.dashboardMenu}>
          <a
            className={classes.dashboardContainer}
            href={dashboardPortal?.urlPath}
            onClick={event =>
              handlePortalClick(event, dashboardPortal?.urlPath)
            }
            data-cy={dashboardPortal.dataCy}
          >
            <Icon style={styles.dashboardIcon}>
              <img
                src={dashboardImage}
                alt="Dashboard"
                className={classes.icon}
              />
            </Icon>
            <div className={classes.dashboardText}>Dashboard</div>
          </a>
          <div className={classes.portalsContainer}>
            {enabledPortals.map(portalKey => {
              const { urlPath, description, image, dataCy } =
                getPortals(preferences)[portalKey];

              return (
                <a
                  className={classes.portal}
                  onClick={event => handlePortalClick(event, urlPath)}
                  href={urlPath}
                  key={portalKey}
                  data-cy={dataCy}
                >
                  <div className={classes.portalIcon}>
                    <img src={image} alt={description} />
                  </div>
                  <div className={classes.portalText}>{description}</div>
                </a>
              );
            })}
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default PortalMenu;
